import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  TrendingUp, TrendingDown, Award, Vote, Users as UsersIcon, CheckCircle,
  XCircle, MinusCircle, Calendar, Zap, Shuffle, Network, GraduationCap,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart,
  Line, XAxis, YAxis, ReferenceLine,
} from "recharts";
import { ordinal, statusPillClass } from "./shared";
import { useAuth } from "@/context/AuthContext";
import { LockedPanel } from "@/components/entitlements/LockedPanel";
import { PressurePoints } from "./PressurePoints";

/* eslint-disable no-unused-vars */
export const VotingTab = ({
  legislator,
  legislatorId,
  navigate,
  saveScroll,
  loading,
  recentVotes,
  surpriseVotes,
  votingTribe,
  flipFlops,
  analyticsLoading,
  getVoteIcon
}) => {
  const { featureMode } = useAuth();
  const fm = featureMode;
  // ⚡ party-break lookup: a vote is anomalous when it appears in Surprise Votes.
  const breakKey = (billId, roll) => `${billId}|${roll ?? ""}`;
  const breaks = new Map((surpriseVotes || []).map((s) => [breakKey(s.billId, s.rollCallNumber), s]));
  const breakFor = (v) => breaks.get(breakKey(v.billId, v.rollCallNumber)) || (surpriseVotes || []).find((s) => s.billId === v.billId && (s.date || "").slice(0, 10) === (v.voteDate || "").slice(0, 10));
  const BreakFlag = ({ v, testId }) => {
    const s = breakFor(v);
    if (!s) return null;
    return (
      <span className="text-orange-500 cursor-help select-none" data-testid={testId}
        title={`Broke with party: voted ${s.memberVote} while most ${s.partyDirection === "Yea" ? "of the party voted Yea" : "of the party voted Nay"} (${s.partyYes}-${s.partyNo}). Defections like this are where persuasion has worked before.`}>⚡</span>
    );
  };
  const breakCount = (surpriseVotes || []).length;
  const totalRolls = votingTribe?.totalRolls || 0;
  const breakRate = totalRolls ? (breakCount / totalRolls) * 100 : null;
  return (
        <div className="space-y-8" data-testid="profile-tab-panel-voting">
            {/* Recent Votes */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2" title="The five most recent roll calls this member cast. A ⚡ marks a vote against their own party's majority.">Recent Votes
                {breakRate != null && breakRate >= 8 && (
                  <span className="text-orange-500 text-lg cursor-help select-none" data-testid="anomaly-break-rate" title={`Unusual defection rate: ${breakCount} of ${totalRolls} tracked roll calls (${breakRate.toFixed(1)}%) went against the party majority. Most members stay under a few percent.`}>⚡</span>
                )}
              </h2>
              <div className="space-y-4">
                {recentVotes.length > 0 ? (
                  recentVotes.slice(0, 5).map((vote, vi) => (
                    <div key={`${vote.id}-${vi}`} className="border-l-4 border-blue-500 bg-gray-50 p-4 rounded-r-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Link
                            to={`/bill/${vote.billId}`}
                            state={{ fromLegislator: legislatorId }}
                            className="text-left text-blue-600 hover:underline block"
                            data-testid={`recent-vote-link-${vote.id}`}
                          >
                            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                              {vote.description || vote.billId}
                            </h3>
                            <p className="text-sm text-blue-600 mb-2 underline">{vote.billId}</p>
                          </Link>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            <span>{vote.voteDate && new Date(vote.voteDate).toLocaleDateString()}</span>
                            <Badge variant="outline" className="text-xs">{vote.chamber}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <BreakFlag v={vote} testId={`anomaly-recent-vote-${vi}`} />
                          {getVoteIcon(vote.vote)}
                          <span className={`text-lg font-bold ${
                            vote.vote === "Yea" ? "text-green-700" : vote.vote === "Nay" ? "text-red-700" : "text-gray-700"
                          }`}>
                            {vote.vote}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">No recent votes recorded yet. Roll-call vote data is being cached, check back soon.</p>
                )}
              </div>
            </Card>

            {/* --- Voting Pattern Analytics: Surprise / Tribe / Flip-Flops --- */}
            {analyticsLoading && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="analytics-loading">
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <p>Loading voting pattern analytics…</p>
                </div>
              </Card>
            )}

            {fm("surprise_votes") === "none" ? (
              <LockedPanel light feature="surprise_votes" tier="pro" title="Surprise Votes"
                blurb="Every roll call where this legislator broke with their party's majority, the defections that matter." />
            ) : !analyticsLoading && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="surprise-votes-card">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-6 h-6 text-amber-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Surprise Votes{surpriseVotes.length > 0 ? ` (${surpriseVotes.length})` : ""}</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Roll calls where this legislator voted <strong>against</strong> their own party's majority. Click any row to read the bill.
                </p>
                {surpriseVotes.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4" data-testid="surprise-votes-empty">
                    No breaks with their party found in the cached roll-call votes, this member has voted
                    with their party's majority on every tracked roll call so far.
                  </p>
                ) : (
                <>
                {(() => {
                  const total = votingTribe?.totalRolls || 0;
                  const counts = surpriseVotes.reduce((m, v) => { const k = v.policyArea || "Uncategorized"; m[k] = (m[k] || 0) + 1; return m; }, {});
                  const [topArea, topN] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0] || ["", 0];
                  const rate = total ? (surpriseVotes.length / total) * 100 : null;
                  const pattern = topN >= 3 || (rate !== null && rate >= 8);
                  const note = pattern
                    ? `Pattern, not a one-off: ${surpriseVotes.length} breaks${rate !== null ? ` (${rate.toFixed(1)}% of tracked roll calls)` : ""}${topN >= 3 ? `, ${topN} of them on ${topArea}` : ""}, this member defects repeatedly${topN >= 3 ? ` on ${topArea.toLowerCase()} measures` : ""}.`
                    : `Isolated incident${surpriseVotes.length > 1 ? "s" : ""}: ${surpriseVotes.length} break${surpriseVotes.length > 1 ? "s" : ""}${rate !== null ? ` across ${total} tracked roll calls (${rate.toFixed(1)}%)` : ""}, spread across topics, no consistent defection pattern.`;
                  return (
                    <p className={`text-sm rounded-lg border px-3 py-2 mb-4 ${pattern ? "bg-orange-50 border-orange-200 text-orange-900" : "bg-gray-50 border-gray-200 text-gray-700"}`} data-testid="surprise-pattern-note">
                      {pattern && <span className="mr-1">⚡</span>}{note}
                    </p>
                  );
                })()}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-amber-50 border-b-2 border-amber-200">
                        <th className="text-left p-3 font-semibold text-gray-700">Date</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Bill</th>
                        <th className="text-center p-3 font-semibold text-gray-700">Their Vote</th>
                        <th className="text-center p-3 font-semibold text-gray-700">Party Went</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Policy Area</th>
                      </tr>
                    </thead>
                    <tbody>
                      {surpriseVotes.map((s, idx) => (
                        <tr
                          key={`${s.billId}-${s.rollCallNumber}-${idx}`}
                          className="border-b border-gray-200 hover:bg-amber-50 cursor-pointer"
                          onClick={() => { saveScroll(); navigate(`/bill/${s.billId}`, { state: { fromLegislator: legislatorId } }); }}
                          data-testid={`surprise-vote-row-${idx}`}
                        >
                          <td className="p-3 text-gray-600">{s.date && new Date(s.date).toLocaleDateString()}</td>
                          <td className="p-3">
                            <Link to={`/bill/${s.billId}`} state={{ fromLegislator: legislatorId }} onClick={(e) => { e.stopPropagation(); saveScroll(); }} className="text-blue-600 hover:underline font-medium">
                              {s.billId}
                            </Link>
                            <div className="text-xs text-gray-500 line-clamp-1 max-w-md">{s.billTitle}</div>
                            {s.whyItHappened && (
                              <p className="mt-1.5 text-xs text-orange-700 leading-snug max-w-xl border-l-2 border-orange-400/60 pl-2" data-testid={`surprise-why-${idx}`}>
                                {s.whyItHappened}
                              </p>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={s.memberVote === "Yea" ? "bg-green-100 text-green-800 border-green-200 border" : "bg-red-100 text-red-800 border-red-200 border"}>
                              {s.memberVote}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Badge className={s.partyDirection === "Yea" ? "bg-green-50 text-green-700 border-green-200 border" : "bg-red-50 text-red-700 border-red-200 border"}>
                                {s.partyDirection}
                              </Badge>
                              <span className="text-xs text-gray-500">{s.partyYes}-{s.partyNo}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {(s.subjects || []).slice(0, 2).map((t) => (
                                <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                )}
              </Card>
            )}

            {fm("legislator_profile_advanced") === "none" ? (
              <LockedPanel light feature="legislator_profile_advanced" tier="pro" title="Voting Tribe"
                blurb="This legislator's closest voting allies and most frequent adversaries, computed from shared roll calls." />
            ) : !analyticsLoading && votingTribe && ((votingTribe.allies?.length || 0) + (votingTribe.adversaries?.length || 0)) > 0 && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="voting-tribe-card">
                <div className="flex items-center gap-2 mb-2">
                  <Network className="w-6 h-6 text-purple-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Voting Tribe</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Closest voting partners and most-opposed peers across the {votingTribe.totalRolls || 0} cached roll-calls this legislator participated in.
                </p>
                {votingTribe.alliesCrossPartyFlag && (
                  <p className="text-sm rounded-lg border border-orange-200 bg-orange-50 text-orange-900 px-3 py-2 mb-4" data-testid="allies-cross-party-flag">
                    ⚡ Unusual: at least one of this member's closest voting allies is from the opposing party, most members' top allies are all same-party.
                  </p>
                )}
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-green-700 mb-2" title="Members who voted the same way as this legislator most often on shared roll calls.">Closest Allies</h3>
                    <ul className="space-y-2">
                      {(votingTribe.allies || []).map((a, i) => (
                        <li key={a.legislatorId || a.name + i} className="flex items-center justify-between gap-3 p-2 rounded bg-green-50 border border-green-100">
                          <div className="min-w-0">
                            {a.legislatorId ? (
                              <Link to={`/legislator/${a.legislatorId}`} className="font-medium text-gray-900 hover:text-blue-600 hover:underline" data-testid={`ally-link-${i}`}>
                                {a.name}
                              </Link>
                            ) : (
                              <span className="font-medium text-gray-900">{a.name}</span>
                            )}
                            <p className="text-xs text-gray-600">{a.party || "?"}-{a.state}{a.crossParty && <span className="ml-1 text-orange-600" title="Cross-party ally, unusual: this member's closest voting partner is from the opposing party." data-testid={`ally-cross-flag-${i}`}>⚡ cross-party</span>}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold text-green-700">{a.agreementPercent}%</p>
                            <p className="text-xs text-gray-500">{a.agreementVotes}/{a.commonVotes}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-red-700 mb-2" title="Members who voted the opposite way most often on shared roll calls.">Most Opposed</h3>
                    <ul className="space-y-2">
                      {(votingTribe.adversaries || []).map((a, i) => (
                        <li key={a.legislatorId || a.name + i} className="flex items-center justify-between gap-3 p-2 rounded bg-red-50 border border-red-100">
                          <div className="min-w-0">
                            {a.legislatorId ? (
                              <Link to={`/legislator/${a.legislatorId}`} className="font-medium text-gray-900 hover:text-blue-600 hover:underline" data-testid={`adversary-link-${i}`}>
                                {a.name}
                              </Link>
                            ) : (
                              <span className="font-medium text-gray-900">{a.name}</span>
                            )}
                            <p className="text-xs text-gray-600">{a.party || "?"}-{a.state}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold text-red-700">{a.agreementPercent}%</p>
                            <p className="text-xs text-gray-500">{a.agreementVotes}/{a.commonVotes}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div data-testid="cross-party-overlap">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2" title="Opposite-party members with the highest vote agreement. Empty means no realistic cross-party partner this session.">Cross-Party Overlap</h3>
                    {(votingTribe.crossParty || []).length === 0 ? (
                      <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="cross-party-empty">
                        No meaningful cross-party voting overlap in the current session
                        {votingTribe.crossPartyBest != null ? `, the closest opposite-party member agrees only ${votingTribe.crossPartyBest}% of the time (threshold ${votingTribe.crossPartyThreshold}%).` : "."}
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {(votingTribe.crossParty || []).map((a, i) => (
                          <li key={a.legislatorId || a.name} className="flex items-center justify-between text-sm border border-blue-100 bg-blue-50/50 rounded-lg px-3 py-2" data-testid={`cross-party-${i}`}>
                            <button type="button" onClick={() => a.legislatorId && navigate(`/legislator/${a.legislatorId}`)} className="text-left font-medium text-gray-900 hover:text-blue-700">
                              {a.name} <span className="text-xs text-gray-500">({a.party}-{a.state})</span>
                            </button>
                            <span className="font-mono font-bold text-blue-700">{a.agreementPercent}%</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Based on cached roll-calls across all warmed bills. Not weighted by party, a legislator who caucuses with one party may show bipartisan allies when most cached votes were bipartisan.
                </p>
              </Card>
            )}

            {fm("position_shifts") === "none" ? (
              <LockedPanel light feature="position_shifts" tier="pro" title="Pressure Points"
                blurb="Where this legislator bends and where they do not, topic by topic: party loyalty, breaks, and how their support is drifting across the session." />
            ) : !analyticsLoading && (
              <PressurePoints data={flipFlops} legislator={legislator} legislatorId={legislatorId} saveScroll={saveScroll} />
            )}
            {/* Complete Voting History Table */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900" title="Every cached roll call this member cast, newest first. A ⚡ marks a vote against their party's majority.">Complete Voting History</h2>
                {recentVotes.length > 0 && (
                  <span className="text-xs text-gray-500" data-testid="voting-history-count">
                    {recentVotes.length} {recentVotes.length === 1 ? "vote" : "votes"}
                  </span>
                )}
              </div>
              {/* Scrollable container, keeps the card compact while letting
                  users browse hundreds of votes without a separate page. */}
              <div className="overflow-auto max-h-[32rem] rounded-lg border border-gray-200" data-testid="voting-history-scroll">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="text-left p-3 font-semibold text-gray-700">Date</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Bill</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Description</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Chamber</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Vote</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentVotes.length > 0 ? (
                      recentVotes.map((vote, vi) => (
                        <tr 
                          key={`${vote.id}-${vi}`}
                          className="border-b border-gray-200 hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => { saveScroll(); navigate(`/bill/${vote.billId}`, { state: { fromLegislator: legislatorId } }); }}
                          data-testid={`voting-history-row-${vote.id}`}
                        >
                          <td className="p-3 text-gray-600">{vote.voteDate && new Date(vote.voteDate).toLocaleDateString()}</td>
                          <td className="p-3 font-medium">
                            <Link
                              to={`/bill/${vote.billId}`}
                              state={{ fromLegislator: legislatorId }}
                              className="text-blue-600 hover:underline"
                              data-testid={`voting-history-bill-link-${vote.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {vote.billId}
                            </Link>
                          </td>
                          <td className="p-3 text-gray-900 max-w-md">
                            <span className="line-clamp-2">{vote.description || 'Roll-call vote'}</span>
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant="outline">{vote.chamber}</Badge>
                          </td>
                          <td className="p-3 text-center">
                            <span className="inline-flex items-center gap-1.5">
                            <BreakFlag v={vote} testId={`anomaly-history-${vi}`} />
                            <Badge className={
                              vote.vote === "Yea" ? "bg-green-100 text-green-800 border-green-200" :
                              vote.vote === "Nay" ? "bg-red-100 text-red-800 border-red-200" :
                              vote.vote === "Present" ? "bg-gray-100 text-gray-800 border-gray-200" :
                              "bg-orange-100 text-orange-800 border-orange-200"
                            }>
                              {vote.vote}
                            </Badge>
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-gray-500">
                          Individual roll-call votes are being aggregated from recent bills. 
                          Browse a bill's detail page to populate more votes into this table.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
        </div>
  );
};
